import { useTranslation } from 'react-i18next'
import { FaCreditCard, FaPaypal, FaUniversity } from 'react-icons/fa'
import { PaymentMethod } from '@/services/payment/paymentService'

interface PaymentFormProps {
  selectedMethod: PaymentMethod | null
  onMethodSelect: (method: PaymentMethod) => void
}

const PaymentForm = ({ selectedMethod, onMethodSelect }: PaymentFormProps) => {
  const { t } = useTranslation()

  const paymentMethods: PaymentMethod[] = [
    {
      type: 'stripe',
      name: t('checkout.paymentMethods.stripe'),
      icon: 'card',
    },
    {
      type: 'paypal',
      name: t('checkout.paymentMethods.paypal'),
      icon: 'paypal',
    },
    {
      type: 'bank_transfer',
      name: t('checkout.paymentMethods.bankTransfer'),
      icon: 'bank',
    },
  ]

  const getIcon = (icon: string) => {
    switch (icon) {
      case 'card':
        return <FaCreditCard className="w-5 h-5" />
      case 'paypal':
        return <FaPaypal className="w-5 h-5" />
      case 'bank':
        return <FaUniversity className="w-5 h-5" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-100 mb-4">
        {t('checkout.paymentMethod')}
      </h3>

      <div className="space-y-3">
        {paymentMethods.map((method) => (
          <button
            key={method.type}
            type="button"
            onClick={() => onMethodSelect(method)}
            className={`
              w-full p-4 rounded-lg border-2 transition-all duration-300
              ${
                selectedMethod?.type === method.type
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-dark-700 bg-dark-800 hover:border-dark-600'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div
                className={`
                  p-2 rounded-lg
                  ${
                    selectedMethod?.type === method.type
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-700 text-gray-400'
                  }
                `}
              >
                {getIcon(method.icon || '')}
              </div>
              <span
                className={`
                  font-semibold
                  ${
                    selectedMethod?.type === method.type
                      ? 'text-primary-400'
                      : 'text-gray-300'
                  }
                `}
              >
                {method.name}
              </span>
              {selectedMethod?.type === method.type && (
                <div className="ml-auto">
                  <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {selectedMethod?.type === 'stripe' && (
        <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <p className="text-sm text-yellow-400">
            {t('checkout.stripeNote')}
          </p>
        </div>
      )}

      {selectedMethod?.type === 'paypal' && (
        <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-blue-400">
            {t('checkout.paypalNote')}
          </p>
        </div>
      )}

      {selectedMethod?.type === 'bank_transfer' && (
        <div className="mt-4 p-4 bg-gray-500/10 border border-gray-500/20 rounded-lg">
          <p className="text-sm text-gray-400">
            {t('checkout.bankTransferNote')}
          </p>
        </div>
      )}
    </div>
  )
}

export default PaymentForm
